import {
  PRICELIST_DETECTION_SAMPLE_BYTES,
  PRICELIST_UPLOAD_MAX_BYTES,
  PRICELIST_XLSB_UPLOAD_MAX_BYTES,
  XLSB_MIME_TYPE,
  ZIP_EOCD_SCAN_BYTES,
  detectFileTypeFromSample,
  detectOle2OfficeKind,
  isPrintablePlainText,
  type DetectedFileType,
} from '@erp/shared-utils';

/** `accept` attribute for pricelist file inputs (PDF, Excel, CSV, XML, Word, Outlook .msg, images). */
export const PRICELIST_UPLOAD_ACCEPT =
  '.msg,application/vnd.ms-outlook,application/pdf,.pdf,.xlsx,.xls,.xlsm,.xlsb,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel.sheet.binary.macroEnabled.12,text/csv,text/plain,.csv,.txt,.xml,text/xml,application/xml,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/gif,image/webp,image/bmp,image/svg+xml,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg';

/**
 * Detect a selected file's type from its header bytes (not its name/extension).
 * Returns null when the file is not a supported pricelist format. Reads only a
 * small leading slice — the file itself can be a gigabyte.
 */
export async function detectUploadedFileType(
  file: File,
): Promise<DetectedFileType | null> {
  const sample = new Uint8Array(
    await file.slice(0, PRICELIST_DETECTION_SAMPLE_BYTES).arrayBuffer(),
  );
  if (file.size <= PRICELIST_DETECTION_SAMPLE_BYTES) {
    return detectFileTypeFromSample(sample);
  }
  const tailLen = Math.min(ZIP_EOCD_SCAN_BYTES, file.size);
  const tailStart = Math.max(0, file.size - tailLen);
  const tail = new Uint8Array(await file.slice(tailStart).arrayBuffer());
  const ole2 = await detectOle2OfficeKind({
    head: sample,
    tail,
    fileSize: file.size,
    readRange: async (offset, length) =>
      new Uint8Array(await file.slice(offset, offset + length).arrayBuffer()),
  });
  return ole2 ?? detectFileTypeFromSample(sample, tail);
}

/** Human-readable byte size, e.g. 10485760 → "10 MB", 512000 → "500 KB". */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb * 10) / 10} KB`;
  const mb = kb / 1024;
  return `${Math.round(mb * 10) / 10} MB`;
}

/**
 * Relative "last upload" label for the pricelist companies list: minutes and
 * hours while recent, then a dd/mm/yyyy date. Null means never uploaded.
 */
export function formatLastUpload(date: Date | null): string {
  if (date === null) return '—';
  const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getFullYear()}`;
}

/**
 * Best-effort human-readable description of the file the browser handed us, for
 * error copy when a file isn't a supported pricelist type. Prefers the
 * browser-reported MIME type, falls back to the file extension, then "unknown".
 */
export function describeReceivedFileType(file: File): string {
  if (file.type) return file.type;
  const dot = file.name.lastIndexOf('.');
  if (dot !== -1 && dot < file.name.length - 1) {
    return file.name.slice(dot).toLowerCase(); // includes the dot, e.g. ".txt"
  }
  return 'unknown';
}

/** Minimal shape of the `gt` translator from gt-react's `useGT()`. */
export type TranslateFn = (
  content: string,
  opts?: Record<string, unknown>,
) => string;

/**
 * Why a chosen file can't be uploaded, already translated for display. Shared by
 * every upload surface so the toast shape and copy stay consistent.
 */
export interface UploadFileRejection {
  kind: 'too-large' | 'unsupported';
  title: string;
  message: string;
}

/** The shared "this file exceeds the limit" rejection. */
function tooLargeRejection(
  file: File,
  maxBytes: number,
  gt: TranslateFn,
): UploadFileRejection {
  return {
    kind: 'too-large',
    title: gt('File too large', {
      context: 'toast title when a dropped file exceeds the upload limit',
    }),
    message: `${gt('The upload limit is', {
      context: 'prefix before the maximum allowed upload size',
    })} ${formatFileSize(maxBytes)}, ${gt('but this file is', {
      context: 'prefix before the size of the file the user tried to upload',
    })} ${formatFileSize(file.size)}.`,
  };
}

/** Extensions the CSV→XLSX converter parses as delimited text. */
export const CSV_UPLOAD_EXTENSIONS_RE = /\.(csv|txt)$/i;

/**
 * Validate a candidate file for the CSV→XLSX converter.
 *
 * Deliberately unlike {@link validatePricelistFile} in exactly two ways: there
 * is no size limit (the converter streams, so memory is bounded by the row, not
 * the file), and the only accepted types are `.csv` and `.txt` — a `.txt` is
 * simply parsed as CSV. The check is by extension because delimited text has no
 * header bytes to detect; the backend parser is the real gate, and it rejects
 * anything it can't read as CSV.
 */
export function validateCsvFile(
  file: File,
  gt: TranslateFn,
): UploadFileRejection | null {
  if (CSV_UPLOAD_EXTENSIONS_RE.test(file.name)) return null;
  return {
    kind: 'unsupported',
    title: gt('Unsupported file format', {
      context: 'toast title when an unsupported file type is selected',
    }),
    message: `${gt('Got a file of type', {
      context:
        'prefix before the type of an unsupported file the user tried to upload',
    })} “${describeReceivedFileType(file)}”. ${gt(
      'Please upload a file ending in .csv or .txt.',
      { context: 'toast message listing the supported CSV upload formats' },
    )}`,
  };
}

export interface ValidatePricelistFileOptions {
  acceptPrintablePlainText?: boolean;
}

function genericUnsupportedRejection(
  file: File,
  gt: TranslateFn,
): UploadFileRejection {
  return {
    kind: 'unsupported',
    title: gt('Unsupported file format', {
      context: 'toast title when an unsupported file type is selected',
    }),
    message: `${gt('Got a file of type', {
      context:
        'prefix before the type of an unsupported file the user tried to upload',
    })} “${describeReceivedFileType(file)}”. ${gt(
      'Please upload a PDF, Excel spreadsheet, CSV, plain text (.txt), XML, Word document (.doc or .docx), Outlook email (.msg), or image (PNG, JPEG, GIF, WEBP, BMP, or SVG).',
      {
        context: 'toast message listing the supported pricelist upload formats',
      },
    )}`,
  };
}

async function sampleLeadingBytes(file: File): Promise<Uint8Array | null> {
  try {
    return new Uint8Array(
      await file.slice(0, PRICELIST_DETECTION_SAMPLE_BYTES).arrayBuffer(),
    );
  } catch {
    return null;
  }
}

async function acceptUndetectedPrintableTxt(
  file: File,
  gt: TranslateFn,
): Promise<UploadFileRejection | null | undefined> {
  if (!/\.txt$/i.test(file.name)) return undefined;
  const sample = await sampleLeadingBytes(file);
  if (sample === null || !isPrintablePlainText(sample)) return undefined;
  const maxBytes = PRICELIST_UPLOAD_MAX_BYTES.csv;
  if (file.size > maxBytes) return tooLargeRejection(file, maxBytes, gt);
  return null;
}

/**
 * Validate a candidate pricelist file by size and header-byte type. Returns a
 * translated rejection to surface to the user, or null when the file is
 * acceptable. Shared by the drag-and-drop area and the upload modal so both
 * enforce the same rules and copy. `gt` is passed in since translation is only
 * available inside components.
 */
export async function validatePricelistFile(
  file: File,
  gt: TranslateFn,
  options?: ValidatePricelistFileOptions,
): Promise<UploadFileRejection | null> {
  // Validate by the file's header bytes, not its name/extension. Reading the
  // file can reject (e.g. a dropped folder), so guard it — otherwise the caller
  // would fail silently with no feedback. Detection comes before the size
  // check because the size limit depends on what the file is.
  let detected: DetectedFileType | null = null;
  try {
    detected = await detectUploadedFileType(file);
  } catch {
    detected = null;
  }
  if (!detected) {
    if (options?.acceptPrintablePlainText) {
      const printable = await acceptUndetectedPrintableTxt(file, gt);
      if (printable !== undefined) return printable;
    }
    return genericUnsupportedRejection(file, gt);
  }
  const maxBytes =
    detected.mimeType === XLSB_MIME_TYPE
      ? PRICELIST_XLSB_UPLOAD_MAX_BYTES
      : PRICELIST_UPLOAD_MAX_BYTES[detected.kind];
  if (file.size > maxBytes) {
    return tooLargeRejection(file, maxBytes, gt);
  }
  return null;
}

export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // intentionally not revoking — download is async in some browsers
}

export interface XhrPutBlobOptions {
  url: string;
  body: Blob;
  contentType: string;
  onUploadProgress: (pct: number) => void;
}

/**
 * PUT raw bytes to a presigned storage URL, with upload progress.
 *
 * An XHR rather than fetch because fetch still has no upload progress events,
 * and a determinate progress bar is the whole point of the uploading phase.
 * No credentials: the URL's signature is the authorization, and the storage
 * host would reject our cookies' origin anyway.
 */
export function xhrPutBlob(opts: XhrPutBlobOptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', opts.url);
    xhr.setRequestHeader('Content-Type', opts.contentType);
    // Azure block blob PUTs require the blob type; S3 ignores unknown headers
    // sent to other hosts, but only Azure gets this one.
    if (opts.url.includes('.blob.core.windows.net')) {
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        opts.onUploadProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        opts.onUploadProgress(100);
        resolve();
      } else {
        reject(
          new Error(
            `Upload to storage failed (HTTP ${xhr.status}). Try again.`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error.'));
    };

    xhr.send(opts.body);
  });
}
